/**
 * ExerciseCard — Per-exercise block in the active workout logger
 *
 * Shows:
 *   - Warm-up sets (read-only display, auto-populated)
 *   - Working set rows with stepper inputs (no keyboard)
 *   - "Add Set" button
 *   - Swap and Plate Calculator triggers
 *   - Autoregulation hint (target RPE + progressive overload suggestion)
 */

import { useState, useCallback, useRef } from 'react';
import { db } from '@/lib/db';
import type {
  GeneratedExercise,
  LoggedSet,
  SuggestedSet,
  WarmupSet,
  RPE,
} from '@/types';
import type { LogSetParams } from '@/hooks/useWorkoutSession';
import { useTrainingPhase } from '@/hooks/useTrainingPhase';
import RestTimer from './RestTimer';
import PlateCalculator from './PlateCalculator';
import SubstitutionModal from './SubstitutionModal';

// ── Stepper Input ─────────────────────────────────────────────

interface StepperInputProps {
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  disabled?: boolean;
  decimals?: number;
  inputMode?: 'decimal' | 'numeric';
  /** Override classes on the number display button (default: 'w-16 h-10 text-lg') */
  numClass?: string;
  /** Override classes on +/− buttons (default: 'w-10 h-10') */
  btnClass?: string;
}

function StepperInput({
  value,
  onChange,
  step = 2.5,
  min = 0,
  disabled = false,
  decimals = 1,
  inputMode = 'decimal',
  numClass = 'w-16 h-10 text-lg',
  btnClass = 'w-10 h-10',
}: StepperInputProps) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const display = decimals === 0
    ? String(value)
    : (value % 1 === 0 ? String(value) : value.toFixed(decimals));

  const handleTapNumber = () => {
    if (disabled) return;
    setRaw(display);
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 30);
  };

  const handleBlur = () => {
    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && parsed >= min) {
      onChange(parsed);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  return (
    <div className="flex items-center gap-1">
      {/* Decrement */}
      <button
        disabled={disabled}
        onPointerDown={(e) => { e.preventDefault(); onChange(Math.max(min, value - step)); }}
        className={`${btnClass} rounded-xl bg-surface-300 flex items-center justify-center
                   text-ink font-bold text-lg select-none
                   active:bg-surface-500 active:scale-95 transition-all
                   disabled:opacity-40`}
      >
        −
      </button>

      {/* Number display / edit */}
      {editing ? (
        <input
          ref={inputRef}
          type="number"
          value={raw}
          inputMode={inputMode}
          autoFocus
          onChange={(e) => setRaw(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`${numClass} bg-surface-300 rounded-xl text-center font-bold
                     font-mono text-ink py-2 focus:outline-none focus:ring-2
                     focus:ring-accent min-h-tap`}
        />
      ) : (
        <button
          disabled={disabled}
          onClick={handleTapNumber}
          className={`${numClass} rounded-xl bg-surface-300 flex items-center justify-center
                     font-bold font-mono text-ink
                     active:bg-surface-400 transition-colors
                     disabled:opacity-70 disabled:bg-transparent`}
        >
          {display}
        </button>
      )}

      {/* Increment */}
      <button
        disabled={disabled}
        onPointerDown={(e) => { e.preventDefault(); onChange(value + step); }}
        className={`${btnClass} rounded-xl bg-surface-300 flex items-center justify-center
                   text-ink font-bold text-lg select-none
                   active:bg-surface-500 active:scale-95 transition-all
                   disabled:opacity-40`}
      >
        +
      </button>
    </div>
  );
}

// ── RPE Segmented Control ─────────────────────────────────────

const RPE_OPTIONS: RPE[] = [6, 7, 8, 9, 10];

function RpeControl({
  value,
  onChange,
  disabled,
}: {
  value: RPE;
  onChange: (v: RPE) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex gap-1">
      {RPE_OPTIONS.map((r) => {
        const active = value === r;
        const color =
          r <= 7 ? (active ? 'bg-success text-white' : 'text-success')
          : r === 8 ? (active ? 'bg-warning text-white' : 'text-warning')
          : (active ? 'bg-danger text-white' : 'text-danger');
        return (
          <button
            key={r}
            disabled={disabled}
            onClick={() => onChange(r)}
            className={`flex-1 h-9 rounded-lg text-sm font-bold transition-all
                        border active:scale-95
                        ${active
                          ? `${color} border-transparent shadow-sm`
                          : `bg-surface-300 border-surface-400 ${color}`}
                        disabled:opacity-40`}
          >
            {r}
          </button>
        );
      })}
    </div>
  );
}

// ── Warm-up Row ──────────────────────────────────────────────

function WarmupRow({ set }: { set: WarmupSet }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className={`w-full flex items-center gap-3 py-2.5 px-1 text-left
                  transition-opacity ${done ? 'opacity-40' : 'opacity-70'}
                  hover:opacity-90 active:scale-[0.99]`}
      onClick={() => setDone((v) => !v)}
    >
      <div className={`w-7 h-7 rounded-full flex items-center justify-center
                       text-xs font-bold flex-shrink-0 transition-colors
                       ${done ? 'bg-success text-white' : 'bg-surface-400 text-ink-muted'}`}>
        {done ? '✓' : `W${set.setNumber}`}
      </div>
      <div className="flex-1 flex items-center gap-2 font-mono text-sm text-ink-muted">
        <span className="w-16 text-center">{set.targetWeightLbs} lbs</span>
        <span className="text-surface-500">×</span>
        <span className="w-10 text-center">{set.targetReps} reps</span>
        <span className="text-surface-500 text-xs ml-auto">{set.restSeconds}s rest</span>
      </div>
    </button>
  );
}

// ── Working Set Row ───────────────────────────────────────────

interface SetRowProps {
  setNumber: number;
  suggested: SuggestedSet;
  logged: LoggedSet | undefined;
  onLog: (params: Omit<LogSetParams, 'exerciseId' | 'isWarmup'>) => void;
  onDelete: (setId: string) => void;
  onUpdate?: (setId: string, patch: Partial<LoggedSet>) => void;
  onRestStart: (seconds: number) => void;
  weightStep: number;
}

function SetRow({
  setNumber,
  suggested,
  logged,
  onLog,
  onDelete,
  onUpdate,
  onRestStart,
  weightStep,
}: SetRowProps) {
  const [weight, setWeight] = useState(
    logged?.weightLbs ?? suggested.suggestedWeightLbs,
  );
  const [reps, setReps] = useState(logged?.reps ?? suggested.targetReps);
  const [rpe, setRpe] = useState<RPE>(logged?.rpe ?? suggested.targetRPE);
  const [editing, setEditing] = useState(false);

  const isLogged = !!logged;

  const handleLog = () => {
    if (isLogged) return;
    onLog({ setNumber, weightLbs: weight, reps, rpe });
    onRestStart(suggested.restSeconds);
  };

  const handleSaveEdit = () => {
    if (logged && onUpdate) {
      onUpdate(logged.id, { weightLbs: weight, reps, rpe });
    }
    setEditing(false);
  };

  const handleDelete = () => {
    if (logged) onDelete(logged.id);
  };

  return (
    <div className={`rounded-xl transition-colors duration-150 px-2 py-2.5
                     ${isLogged && !editing ? 'bg-success/5' : 'bg-transparent'}`}>

      {/* Controls row */}
      <div className="flex items-center gap-2">
        {/* Set badge */}
        <div className={`w-7 h-7 rounded-full flex items-center justify-center
                         text-xs font-bold flex-shrink-0 transition-colors
                         ${isLogged && !editing ? 'bg-success text-white' : 'bg-surface-400 text-ink-muted'}`}>
          {isLogged && !editing ? '✓' : setNumber}
        </div>

        {/* Weight — primary value, larger display */}
        <StepperInput
          value={weight}
          onChange={setWeight}
          step={weightStep}
          disabled={isLogged && !editing}
          decimals={weight % 1 !== 0 ? 1 : 0}
          inputMode="decimal"
          numClass="w-16 h-11 text-2xl"
          btnClass="w-9 h-11"
        />

        <span className="text-ink-faint text-sm flex-shrink-0">×</span>

        {/* Reps — secondary value, smaller */}
        <StepperInput
          value={reps}
          onChange={setReps}
          step={1}
          min={1}
          disabled={isLogged && !editing}
          decimals={0}
          inputMode="numeric"
          numClass="w-11 h-11 text-lg"
          btnClass="w-9 h-11"
        />

        {/* Log / edit / save button */}
        {editing ? (
          <button
            onClick={handleSaveEdit}
            className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center
                       justify-center bg-accent text-white transition-all active:scale-90"
          >
            ✓
          </button>
        ) : isLogged ? (
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={() => { setEditing(true); }}
              className="w-10 h-11 rounded-xl flex items-center justify-center
                         bg-surface-300 text-ink-muted transition-all active:scale-90 text-sm"
              title="Edit set"
            >
              ✏
            </button>
            <button
              onClick={handleDelete}
              className="w-10 h-11 rounded-xl flex items-center justify-center
                         bg-danger/10 text-danger transition-all active:scale-90 text-sm"
              title="Delete set"
            >
              ✕
            </button>
          </div>
        ) : (
          <button
            onClick={handleLog}
            className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center
                       justify-center bg-accent text-white shadow-glow-accent
                       transition-all duration-150 active:scale-90 text-lg font-bold"
          >
            ✓
          </button>
        )}
      </div>

      {/* RPE control */}
      <div className="mt-2.5 pl-9 pr-1">
        <RpeControl
          value={rpe}
          onChange={setRpe}
          disabled={isLogged && !editing}
        />
      </div>

      {/* Inline note — shown after logging */}
      {isLogged && (
        <NoteInput
          initialNote={logged?.notes ?? ''}
          onSave={(note) => {
            if (logged) db.loggedSets.update(logged.id, { notes: note });
          }}
        />
      )}
    </div>
  );
}

function NoteInput({ initialNote, onSave }: { initialNote: string; onSave: (n: string) => void }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(initialNote);

  if (!open) {
    return (
      <button
        className="text-xs text-ink-faint pl-9 py-0.5 text-left w-full
                   hover:text-ink-muted transition-colors mt-1"
        onClick={() => setOpen(true)}
      >
        {note ? `📝 ${note}` : '+ Add note'}
      </button>
    );
  }

  return (
    <div className="flex gap-2 pl-9 animate-fade-in mt-1">
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note for this set…"
        autoFocus
        className="flex-1 bg-surface-300 rounded-lg px-3 py-2 text-xs text-ink
                   focus:outline-none focus:ring-1 focus:ring-accent"
      />
      <button
        className="text-xs font-medium text-accent px-2"
        onClick={() => { onSave(note); setOpen(false); }}
      >
        Save
      </button>
    </div>
  );
}

// ── Form Cue ──────────────────────────────────────────────────

function FormCue({ cue }: { cue: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-accent/80 hover:text-accent
                   transition-colors w-full text-left"
      >
        <span>💡</span>
        <span className="font-medium">Coaching tip</span>
        <span className={`ml-auto text-ink-faint transition-transform ${open ? 'rotate-180' : ''}`}>›</span>
      </button>
      {open && (
        <p className="mt-1.5 text-xs text-ink-muted leading-relaxed bg-surface-200
                      rounded-xl px-3 py-2 animate-fade-in">
          {cue}
        </p>
      )}
    </div>
  );
}

// ── Exercise Card ─────────────────────────────────────────────

interface ExerciseCardProps {
  genExercise: GeneratedExercise;
  index: number;
  loggedSets: LoggedSet[];
  availableEquipment: string[];
  usageCount: Map<string, number>;
  audioEnabled: boolean;
  weightUnit?: 'lbs' | 'kg';
  collapsed?: boolean;
  onExpand?: () => void;
  onLogSet: (params: LogSetParams) => void;
  onDeleteSet: (setId: string) => void;
  onUpdateSet?: (setId: string, patch: Partial<LoggedSet>) => void;
  onSubstitute: (index: number, exercise: import('@/types').Exercise) => void;
}

export default function ExerciseCard({
  genExercise,
  index,
  loggedSets,
  availableEquipment,
  usageCount,
  audioEnabled,
  weightUnit = 'lbs',
  collapsed = false,
  onExpand,
  onLogSet,
  onDeleteSet,
  onUpdateSet,
  onSubstitute,
}: ExerciseCardProps) {
  const { exercise, warmupSets, workingSets } = genExercise;
  const trainingPhase = useTrainingPhase();
  const [showTimer, setShowTimer] = useState(false);
  const [timerDuration, setTimerDuration] = useState(90);
  const [showPlates, setShowPlates] = useState(false);
  const [showSubstitute, setShowSubstitute] = useState(false);
  const [extraSets, setExtraSets] = useState<SuggestedSet[]>([]);
  const [warmupsExpanded, setWarmupsExpanded] = useState(false);

  const primaryMuscle = exercise.muscles[0]?.muscle;
  const isBarbell = exercise.equipment.includes('barbell') || exercise.equipment.includes('trap_bar');

  // Weight step: 2.5 for barbell/dumbbell, 5 for machine/cable, 1 for bodyweight
  const weightStep =
    exercise.equipment.includes('barbell') || exercise.equipment.includes('dumbbell') ||
    exercise.equipment.includes('ez_bar') || exercise.equipment.includes('trap_bar')
      ? 2.5
      : exercise.equipment.includes('bodyweight') ? 1
      : 5;

  const allWorkingSets = [...workingSets, ...extraSets];

  const handleLogSet = useCallback((params: Omit<LogSetParams, 'exerciseId' | 'isWarmup'>) => {
    onLogSet({ ...params, exerciseId: exercise.id, isWarmup: false });
  }, [exercise.id, onLogSet]);

  const handleRestStart = useCallback((seconds: number) => {
    setTimerDuration(seconds);
    setShowTimer(true);
  }, []);

  const addExtraSet = () => {
    const last = allWorkingSets[allWorkingSets.length - 1];
    setExtraSets((prev) => [
      ...prev,
      {
        setNumber: allWorkingSets.length + 1,
        suggestedWeightLbs: last?.suggestedWeightLbs ?? 0,
        targetReps: last?.targetReps ?? 8,
        targetRPE: last?.targetRPE ?? 8,
        restSeconds: exercise.restSeconds,
      },
    ]);
  };

  const loggedByNumber = new Map(loggedSets.map((s) => [s.setNumber, s]));
  const loggedCount = loggedSets.filter((s) => !s.isWarmup).length;
  const totalSets = allWorkingSets.length;
  const progressPct = totalSets > 0 ? (loggedCount / totalSets) * 100 : 0;
  const allDone = loggedCount > 0 && loggedCount >= totalSets;

  const suggestedWeight = workingSets[0]?.suggestedWeightLbs ?? 0;

  // ── Collapsed list-row rendering ─────────────────────────────
  if (collapsed) {
    const inProgress = loggedCount > 0 && !allDone;
    return (
      <button
        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-left
                    transition-all duration-150 active:scale-[0.98]
                    ${allDone
                      ? 'bg-success/8 border border-success/20'
                      : 'bg-surface-200 border border-surface-400'}`}
        onClick={onExpand}
      >
        {/* Index bubble */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center
                         text-xs font-bold flex-shrink-0 transition-colors
                         ${allDone ? 'bg-success text-white' : 'bg-surface-400 text-ink-muted'}`}>
          {allDone ? '✓' : index + 1}
        </div>

        {/* Name + muscle + suggested weight */}
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm leading-tight truncate ${allDone ? 'text-ink-muted' : 'text-ink'}`}>
            {exercise.name}
          </p>
          <p className="text-xs text-ink-faint mt-0.5">
            {primaryMuscle?.replace(/_/g, ' ')}
            {suggestedWeight > 0 && !allDone && (
              <span className="text-ink-muted font-medium">
                {` · ${suggestedWeight} ${weightUnit} × ${workingSets[0]?.targetReps ?? '?'}`}
              </span>
            )}
          </p>
        </div>

        {/* Set progress chip */}
        <div className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0
                         ${allDone
                           ? 'bg-success/15 text-success'
                           : inProgress
                             ? 'bg-accent/15 text-accent'
                             : 'bg-surface-400 text-ink-muted'}`}>
          {allDone ? '✓ done' : inProgress ? `${loggedCount}/${totalSets}` : `${totalSets} sets`}
        </div>

        <span className="text-ink-faint text-sm flex-shrink-0">›</span>
      </button>
    );
  }

  return (
    <>
      <div className="card space-y-3">
        {/* Exercise header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Muscle + category inline badges */}
            {primaryMuscle && (
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-xs text-ink-faint">
                  {primaryMuscle.replace(/_/g, ' ')}
                </span>
                {exercise.category === 'compound' && (
                  <span className="text-[10px] font-semibold text-accent/80 bg-accent/10
                                   px-1.5 py-0.5 rounded-full">
                    compound
                  </span>
                )}
              </div>
            )}
            <h3 className="font-bold text-ink text-lg leading-tight">
              {exercise.name}
            </h3>
          </div>

          {/* Action buttons */}
          <div className="flex gap-1 flex-shrink-0">
            {isBarbell && (
              <button
                className="w-9 h-9 rounded-xl bg-surface-300 flex items-center justify-center
                           text-base hover:bg-surface-400 active:scale-90 transition-all"
                onClick={() => setShowPlates(true)}
                title="Plate calculator"
              >
                🏋️
              </button>
            )}
            <button
              className="w-9 h-9 rounded-xl bg-surface-300 flex items-center justify-center
                         text-base hover:bg-surface-400 active:scale-90 transition-all"
              onClick={() => setShowSubstitute(true)}
              title="Swap exercise"
            >
              ⇄
            </button>
          </div>
        </div>

        {/* Form cue */}
        {exercise.formCue && <FormCue cue={exercise.formCue} />}

        {/* Phase coaching banner */}
        {!collapsed && (
          <div className="rounded-xl px-3 py-2.5 flex items-start gap-2.5"
               style={{ background: 'var(--c-surface-300)' }}>
            <div className="flex-shrink-0 mt-0.5">
              {/* Four-dot phase indicator */}
              <div className="flex gap-0.5">
                {([1, 2, 3, 4] as const).map((n) => (
                  <div
                    key={n}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      n <= trainingPhase.phase
                        ? trainingPhase.color.replace('text-', 'bg-')
                        : 'bg-surface-400'
                    }`}
                  />
                ))}
              </div>
            </div>
            <div className="min-w-0">
              <p className={`text-xs font-semibold leading-tight ${trainingPhase.color}`}>
                Phase {trainingPhase.phase} of 4 — {trainingPhase.label}
              </p>
              <p className="text-xs text-ink-muted mt-0.5 leading-snug">{trainingPhase.cue}</p>
            </div>
          </div>
        )}

        {/* Autoregulation hint */}
        {suggestedWeight > 0 && (
          <div className="bg-accent/10 rounded-xl px-3 py-2 flex items-center justify-between">
            <div>
              <p className="text-xs text-accent font-medium">Target</p>
              <p className="text-sm font-bold text-ink">
                {suggestedWeight} {weightUnit} × {workingSets[0]?.targetReps} reps
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-ink-muted">RPE goal</p>
              <p className="text-sm font-bold text-ink">{workingSets[0]?.targetRPE}</p>
            </div>
          </div>
        )}

        {/* Progress bar */}
        {totalSets > 0 && (
          <div className="h-1.5 bg-surface-400 rounded-full overflow-hidden">
            <div
              className="h-full bg-success rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        )}

        {/* Warm-up sets — collapsed by default */}
        {warmupSets.length > 0 && (
          <div>
            <button
              onClick={() => setWarmupsExpanded((v) => !v)}
              className="flex items-center gap-2 text-xs text-ink-faint hover:text-ink-muted
                         transition-colors px-1 w-full text-left mb-1"
            >
              <span className="uppercase tracking-widest">Warm-up · {warmupSets.length} sets</span>
              <span className={`ml-auto transition-transform ${warmupsExpanded ? 'rotate-180' : ''}`}>›</span>
            </button>
            {warmupsExpanded && (
              <div className="divide-y divide-surface-400 animate-fade-in">
                {warmupSets.map((ws) => (
                  <WarmupRow key={ws.setNumber} set={ws} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Working sets */}
        {allWorkingSets.length > 0 && (
          <div className="space-y-1">
            {allWorkingSets.map((s) => (
              <SetRow
                key={s.setNumber}
                setNumber={s.setNumber}
                suggested={s}
                logged={loggedByNumber.get(s.setNumber)}
                onLog={handleLogSet}
                onDelete={onDeleteSet}
                onUpdate={onUpdateSet}
                onRestStart={handleRestStart}
                weightStep={weightStep}
              />
            ))}
          </div>
        )}

        {/* Add extra set */}
        <button
          className="btn-ghost text-sm w-full py-2 border border-dashed border-surface-400
                     rounded-xl text-ink-faint hover:text-ink-muted hover:border-surface-500"
          onClick={addExtraSet}
        >
          + Add Set
        </button>
      </div>

      {/* Overlays */}
      {showTimer && (
        <RestTimer
          durationSeconds={timerDuration}
          audioEnabled={audioEnabled}
          onComplete={() => setShowTimer(false)}
          onSkip={() => setShowTimer(false)}
        />
      )}

      {showPlates && (
        <PlateCalculator
          targetWeight={suggestedWeight}
          weightUnit={weightUnit}
          onClose={() => setShowPlates(false)}
        />
      )}

      {showSubstitute && primaryMuscle && (
        <SubstitutionModal
          targetMuscle={primaryMuscle}
          currentExerciseId={exercise.id}
          availableEquipment={availableEquipment}
          usageCount={usageCount}
          onSelect={(replacement) => onSubstitute(index, replacement)}
          onClose={() => setShowSubstitute(false)}
        />
      )}
    </>
  );
}
